import {
  CalendarOutlined,
  CarOutlined,
  DollarOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
} from "antd";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BillingSubNav } from "~/components/admin/billing";
import PageMeta from "~/components/common/meta";
import { useGetExpenseInvoices } from "~/services/expense/expense.queries";
import { ExpenseCategory, TExpense } from "~/types/expense";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CATEGORY_OPTIONS = [
  {
    value: ExpenseCategory.SUPPLIES,
    label: "Supplies",
    icon: <ShoppingCartOutlined />,
  },
  {
    value: ExpenseCategory.TRANSPORTATION,
    label: "Transportation",
    icon: <CarOutlined />,
  },
  {
    value: ExpenseCategory.EQUIPMENT,
    label: "Equipment",
    icon: <ToolOutlined />,
  },
  {
    value: ExpenseCategory.OFFICE,
    label: "Office",
    icon: <FileTextOutlined />,
  },
  {
    value: ExpenseCategory.INSURANCE,
    label: "Insurance",
    icon: <TeamOutlined />,
  },
  { value: ExpenseCategory.MAINTENANCE, label: "Maintenance" },
  { value: ExpenseCategory.UTILITIES, label: "Utilities" },
  { value: ExpenseCategory.OTHER, label: "Other" },
];

export const ScreenExpenseInvoices: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedExpense, setSelectedExpense] = useState<TExpense | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // const notification = useNotification();
  const navigate = useNavigate();

  // Query params
  const params = useMemo(() => {
    const p: any = { page: 1, pageSize: 10 };
    if (searchText) p.search = searchText;
    if (categoryFilter !== "all") p.category = categoryFilter;
    if (dateRange && dateRange[0] && dateRange[1]) {
      p.startDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
      p.endDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
    }
    return p;
  }, [searchText, categoryFilter, dateRange]);

  // Data hooks
  const { data: invoicesData, isLoading } = useGetExpenseInvoices(params);
  // const { data: vendorsData } = useGetVendors({ page: 1, pageSize: 100 });
  // const { data: projectsData } = useGetProjects({ page: 1, pageSize: 100 });
  // const vendors = vendorsData?.items || [];
  // const projects = projectsData?.items || [];

  // Debug logging
  console.log("Invoices Data:", invoicesData);
  console.log("Invoices Items:", invoicesData?.items);

  // Calculate stats
  const totalInvoiced =
    invoicesData?.items?.reduce((sum, item) => {
      const amount = Number(item.amount) || 0;
      return sum + amount;
    }, 0) || 0;
  const totalInvoices = invoicesData?.meta?.totalItems || 0;

  // Table columns
  const columns = [
    {
      title: "Invoice Date",
      dataIndex: "paidAt",
      key: "paidAt",
      render: (paidAt: string) => (
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-gray-400" />
          {dayjs(paidAt).format("MMM DD, YYYY")}
        </div>
      ),
      sorter: (a: TExpense, b: TExpense) =>
        dayjs(a.paidAt).unix() - dayjs(b.paidAt).unix(),
    },
    {
      title: "Expense Ref",
      dataIndex: "expenseRef",
      key: "expenseRef",
      render: (expenseRef: string) => (
        <div className="font-medium text-gray-900">{expenseRef}</div>
      ),
    },
    {
      title: "Vendor",
      dataIndex: ["vendor", "name"],
      key: "vendor",
      render: (_: any, record: TExpense) => (
        <div className="font-medium text-gray-900">
          {record.vendor?.name || "-"}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category: ExpenseCategory) => {
        const found = CATEGORY_OPTIONS.find((c) => c.value === category);
        return (
          <div className="flex items-center">
            <span className="mr-2 text-blue-500">{found?.icon}</span>
            {found?.label || category}
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string, record: TExpense) => (
        <div>
          <div className="text-gray-900">{description}</div>
          {record.project?.name && (
            <div className="text-sm text-gray-500">
              Project: {record.project.name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <div className="font-semibold text-lg text-gray-900">
          ${Number(amount).toFixed(2)}
        </div>
      ),
      sorter: (a: TExpense, b: TExpense) => a.amount - b.amount,
    },
    {
      title: "Status",
      key: "status",
      render: () => (
        <Tag color="green" icon={<DollarOutlined />}>
          Invoice Created
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TExpense) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedExpense(record);
              setIsDetailModalOpen(true);
            }}
          >
            View
          </Button>
          {record.receiptUrl && (
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => window.open(record.receiptUrl, "_blank")}
            >
              Receipt
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageMeta
        title="Expense Invoices | Art Handler"
        description="View expense invoices created in QuickBooks"
      />

      <BillingSubNav activeKey="expenses" />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Invoiced"
              value={totalInvoiced}
              precision={2}
              valueStyle={{ color: "#3f8600" }}
              prefix={<DollarOutlined />}
              formatter={(value) => {
                const numValue = Number(value);
                return isNaN(numValue) ? "$0.00" : `$${numValue.toFixed(2)}`;
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Invoices"
              value={totalInvoices}
              valueStyle={{ color: "#1890ff" }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Controls */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search invoices..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="max-w-md"
            />
          </Col>
          <Col>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              className="w-40"
            >
              <Option value="all">All Categories</Option>
              {CATEGORY_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <RangePicker
              className="w-64"
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col>
            <Button
              onClick={() => navigate("/billing/expenses")}
              className="border-gray-300 hover:bg-gray-50"
            >
              Back to Expenses
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Invoices Table */}
      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={invoicesData?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} invoices`,
            total: invoicesData?.meta?.totalItems ?? 0,
          }}
          locale={{
            emptyText: "No expense invoices found.",
          }}
          className="expense-invoices-table"
        />
      </Card>

      {/* Modal View Details */}
      <Modal
        title="Expense Invoice Details"
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={null}
        width={500}
      >
        {selectedExpense && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">
                <DollarOutlined className="mr-2 text-green-500" />$
                {Number(selectedExpense.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
              <Tag color="green" icon={<DollarOutlined />}>
                Invoice Created
              </Tag>
            </div>
            <Divider className="my-2" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="text-gray-500">Expense Ref:</div>
              <div className="font-medium">{selectedExpense.expenseRef}</div>
              <div className="text-gray-500">Invoice Date:</div>
              <div>{dayjs(selectedExpense.paidAt).format("MMM DD, YYYY")}</div>
              <div className="text-gray-500">Vendor:</div>
              <div>{selectedExpense.vendor?.name || "-"}</div>
              <div className="text-gray-500">Category:</div>
              <div>{selectedExpense.category}</div>
              <div className="text-gray-500">Project:</div>
              <div>{selectedExpense.project?.name || "-"}</div>
            </div>
            <Divider className="my-2" />
            <div>
              <div className="text-gray-500 mb-1">Description:</div>
              <div>{selectedExpense.description}</div>
            </div>
            {selectedExpense.notes && (
              <div>
                <div className="text-gray-500 mb-1">Notes:</div>
                <div>{selectedExpense.notes}</div>
              </div>
            )}
            {selectedExpense.receiptUrl && (
              <div className="mt-4">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    window.open(selectedExpense.receiptUrl, "_blank")
                  }
                >
                  Download Receipt
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ScreenExpenseInvoices;
